import { PHONE_METHOD } from "@constants/form-constants";
import { VerificationFormState } from "@idw-types/state";
import { Logger } from '@utils/logger';
import { VerificationFormActionTypes, VerificationFormActions } from "../actions/verification-form-actions";
import { AttributeStatusTypes } from "@enums/attribute-status-types";
import { AttrListItem, AttrListItemAttribute, AttributeInput } from "@idw-types/data";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { OOBStatusTypes } from "@enums/oob-status-types";
import { getDefaultFormInputValues } from "@utils/attribute-input-util";
import { checkIsOOBComplete } from "@utils/verification-form-util";
import { VerifiedAttribute } from "@idw-types/data/verified-attribute";
import { RootActions, RootActionTypes } from "../actions/root-actions";
import { InternationalAddressCountryTypes } from "@iddataweb/idw-common-ui/enums";

export const initialVerificationFormState: VerificationFormState = {
    attributeData: null,
    previouslySavedAttributeData: null,
    shouldRetryVerification: false,
    attempt: 0,
    retryLimit: 1,
    countryCode: InternationalAddressCountryTypes.US,
    asyncTimeoutID: null,
    allowCountrySelection: true,
    asyncCompleted: false,
    allowDCUIRetry: true,
    dcuiCancelCount: 0,
    dcuiCancelMax: 3,
    needsToScroll: false,
    isOOBComplete: false,
    isAsyncComplete: false,
    asyncButtonText: 'common.button.async',
    showAsync: false,
    disableAsync: false,
    disableSubmit: false,
    showAsyncModal: false,
    showAsyncModalCancelButton: true,
    asyncModalMessage: 'common.asyncModal.waiting',
    assertionErrorMessages: [],
    verificationProcessed: false,
    focusedAttributeID: null,
    asyncMethod: PHONE_METHOD,
    initialized: false,
    isDcuiProcessCompleted: false,
    explicitConsentChecked: false
};

export const verificationFormReducer = (
    state: VerificationFormState = initialVerificationFormState,
    action: VerificationFormActions | RootActions
): VerificationFormState => {
    switch (action.type) {
        case VerificationFormActionTypes.SET_VERIFICATION_FORM: {
            let attributeListWithState: Array<AttributeFormState> = action.payload.attributeInputList.map((attribute) =>
                initializeAttributeFormState(attribute)
            );
            attributeListWithState = setupAttributeDependencies(attributeListWithState);

            return {
                ...state,
                attributeData: attributeListWithState,
                attempt: 0,
                initialized: true
            };
        }
        case VerificationFormActionTypes.ADD_NEW_ATTRIBUTE_VALUE:
            return {
                ...state,
                attributeData: addNewAttributeValue(state.attributeData, action.payload.verifiedAttribute, action.payload.inputData)
            };
        case VerificationFormActionTypes.SET_ATTRIBUTE_SUBMIT_VALUE:
            return {
                ...state,
                attributeData: setAttributeSubmitValue(state.attributeData, action.payload.attribute, action.payload.inputData)
            };
        case VerificationFormActionTypes.SET_ATTRIBUTE_PICKER_VALUE:
            return {
                ...state,
                attributeData: setAttributePickerValue(state.attributeData, action.payload.attribute, action.payload.pickerData)
            };
        case VerificationFormActionTypes.UPDATE_ATTRIBUTE_VERIFIED_STATUSES:
            return {
                ...state,
                attributeData: updateAttributeVerifiedStatuses(state.attributeData, action.payload.verifiedAttributes, action.payload.attempt, action.payload.allowDCUIretry)
            };
        case VerificationFormActionTypes.UPDATE_ATTRIBUTE_ASYNC_STATUS:
            return {
                ...state,
                attributeData: updateAttributeAsyncStatus(state.attributeData, action.payload.attribute)
            };
        case VerificationFormActionTypes.CLEAR_ATTRIBUTE_SUBMIT_VALUES:
            return {
                ...state,
                attributeData: clearAttributeSubmitValues(state.attributeData, action.payload.attribute)
            }
        case VerificationFormActionTypes.SET_IS_OOB_COMPLETE:
            return {
                ...state,
                isOOBComplete: action.payload.isOOBComplete
            };
        case VerificationFormActionTypes.CHECK_OOB_COMPLETE:
            return {
                ...state,
                isOOBComplete: checkIsOOBComplete(state.attributeData)
            };
        case VerificationFormActionTypes.SET_IS_ASYNC_COMPLETE:
            return {
                ...state,
                isAsyncComplete: action.payload.isAsyncComplete
            }
        case VerificationFormActionTypes.SET_ASYNC_COMPLETED:
            return {
                ...state,
                asyncCompleted: action.payload.isCompleted
            };
        case VerificationFormActionTypes.SET_SHOW_ASYNC_BUTTON:
            return {
                ...state,
                showAsync: action.payload.showAsyncButton
            };
        case VerificationFormActionTypes.SET_ASSERTION_ERROR_MESSAGES:
            return {
                ...state,
                assertionErrorMessages: [...action.payload.messages]
            };
        case VerificationFormActionTypes.DISABLE_ASYNC_BUTTON:
            return {
                ...state,
                disableAsync: action.payload.disableAsyncButton
            };
        case VerificationFormActionTypes.DISABLE_SUBMIT_BUTTON:
            return {
                ...state,
                disableSubmit: action.payload.disableSubmitButton
            };
        case VerificationFormActionTypes.SET_ASYNC_BUTTON_TEXT:
            return {
                ...state,
                asyncButtonText: action.payload.asyncButtonText
            };
        case VerificationFormActionTypes.SET_SHOW_ASYNC_MODAL:
            return {
                ...state,
                showAsyncModal: action.payload.show
            };
        case VerificationFormActionTypes.SET_SHOW_ASYNC_MODAL_CANCEL_BUTTON:
            return {
                ...state,
                showAsyncModalCancelButton: action.payload.show
            };
        case VerificationFormActionTypes.SET_ASYNC_MODAL_MESSAGE:
            return {
                ...state,
                asyncModalMessage: action.payload.message
            };
        case VerificationFormActionTypes.SET_ASYNC_TIMEOUT_ID:
            return {
                ...state,
                asyncTimeoutID: action.payload.timeoutId
            };
        case VerificationFormActionTypes.SET_DCUI_CANCEL_COUNT:
            return {
                ...state,
                dcuiCancelCount: action.payload.count
            };
        case VerificationFormActionTypes.SET_DCUI_CANCEL_MAX:
            return {
                ...state,
                dcuiCancelMax: action.payload.max
            };
        case VerificationFormActionTypes.SET_RETRY_VERIFICATION:
            return {
                ...state,
                shouldRetryVerification: action.payload.retryVerification
            };
        case VerificationFormActionTypes.UPDATE_ATTEMPT:
            return {
                ...state,
                attempt: action.payload.attempt
            };
        case VerificationFormActionTypes.SET_VERIFICATION_PROCESSED:
            return {
                ...state,
                verificationProcessed: action.payload.processed
            };
        case VerificationFormActionTypes.SET_ATTRIBUTE_IN_FOCUS:
            return {
                ...state,
                focusedAttributeID: action.payload.attributeId
            };
        case VerificationFormActionTypes.SET_ATTRIBUTE_FORM_STATE_IS_READ_ONLY: {
            return {
                ...state,
                attributeData: state.attributeData.map(afs => {
                    if (afs.id === action.payload.attributeId) {
                        return { ...afs, isReadOnly: action.payload.isReadOnly };
                    } else {
                        return afs;
                    }
                })
            };
        }

        case VerificationFormActionTypes.SET_IS_DCUI_PROCESS_COMPLETED: {
            return {
                ...state,
                isDcuiProcessCompleted: action.payload.isDcuiProcessCompleted
            }
        }
        case VerificationFormActionTypes.SET_EXPLICIT_CONSENT_CHECKED: {
            return {
                ...state,
                explicitConsentChecked: action.payload.checked
            }
        }
        case VerificationFormActionTypes.FOCUS_ON_NEXT_ATTRIBUTE: {
            const focusedAttribute = state.attributeData.find(afs => afs.id === state.focusedAttributeID);
            const positionFocusedAttributeId = state.attributeData.indexOf(focusedAttribute);
            if (positionFocusedAttributeId + 1 < state.attributeData.length) {
                return {
                    ...state,
                    focusedAttributeID: state.attributeData[positionFocusedAttributeId + 1].id
                }
            } else {
                return state;
            }
        }
        case VerificationFormActionTypes.SET_ASYNC_METHOD:
            return {
                ...state,
                asyncMethod: action.payload.method
            };
        case RootActionTypes.RESET_STATE:
            return { ...initialVerificationFormState };
        default:
            return state;
    }
};

const addNewAttributeValue = (
    attributeFormStates: Array<AttributeFormState>,
    attribute: AttributeInput,
    inputData: any
) => {
    return attributeFormStates.map(afs => {
        if (afs.id === attribute.id) {
            const inputDataKeys = Object.keys(afs.inputData);
            const filledInputData: { [key: string]: any } = {};
            inputDataKeys.forEach((key: string) => {
                filledInputData[key] = inputData[key] || "";
            })
            const newAttributeFormState: AttributeFormState = {
                ...afs,
                status: AttributeStatusTypes.NEW,
                isNew: true,
                isDisabled: false,
                oobStatus: afs.originalOOBStatus,
                isValueFromPicker: false,
                inputData: filledInputData,
                attrList: [],
                hasPickerDefaultValue: false,
                hasPreviousPickerValues: false,
                hasNoPreSelectedAttributePickerValue: true,
                pickerDefaultValue: null,
                pickerDefaultAttribute: null,
                pickerData: {},
                isReadOnly: false
            };

            return newAttributeFormState
        } else {
            return afs;
        }
    });
};

const setAttributeSubmitValue = (attributeFormStates: Array<AttributeFormState>, attribute: AttributeInput, inputData: any) => { //todo - fix
    return attributeFormStates.map(afs => {
        if (afs.id === attribute.id) {
            return {
                ...afs,
                inputData: { ...afs.inputData, ...inputData }
            }
        } else {
            return afs;
        }
    });
};

const setAttributePickerValue = (attributeFormStates: Array<AttributeFormState>, attribute: AttributeInput, pickerData: any) => { //todo - fix
    return attributeFormStates.map(afs => {
        if (afs.id === attribute.id) {
            return {
                ...afs,
                isValueFromPicker: true,
                pickerData: { ...afs.pickerData, ...pickerData }
            };
        } else {
            return afs;
        }
    });
};

const updateAttributeVerifiedStatuses = (attributeFormStates: Array<AttributeFormState>, verifiedAttributes: Array<VerifiedAttribute>, attempt: number, allowDCUIretry: boolean) => {
    let newAttributeFormStates: Array<AttributeFormState> = [...attributeFormStates];

    verifiedAttributes.forEach(va => {
        const attributeFormState = newAttributeFormStates.find(afs => afs.id === va.id);
        let updatedProperties = {};
        switch (va.status) {
            case AttributeStatusTypes.PASS:
                updatedProperties = getPassStatusProperties(va);
                break;
            case AttributeStatusTypes.PIN:
                updatedProperties = getPinStatusProperties(va, attributeFormState, attempt);
                break;
            case AttributeStatusTypes.NEW:
                updatedProperties = getNewStatusProperties(va, attributeFormState, allowDCUIretry);
                break;
            case AttributeStatusTypes.PICKER:
                updatedProperties = getPickerStatusProperties(va, attributeFormState, allowDCUIretry);
                break;
            case AttributeStatusTypes.FAIL:
                updatedProperties = getFailStatusProperties(va);
                break;
            default:
                Logger.info(`There was a problem with changing the verified attribute status . Got Status:${attributeFormState.status}`);
        }

        newAttributeFormStates = newAttributeFormStates.map(afs => {
            if (afs.id === va.id) {
                return {
                    ...afs,
                    ...updatedProperties
                }
            } else {
                return afs;
            }
        });
    });

    return newAttributeFormStates;
};

const updateAttributeAsyncStatus = (attributeFormStates: Array<AttributeFormState>, attribute: AttributeInput) => {
    return attributeFormStates.map(afs => {
        if (afs.id === attribute.id) {
            const updatedProperties = {
                status: AttributeStatusTypes.NEW,
                oobStatus: OOBStatusTypes.COMPLETE,
                selectedAttributeId: attribute.attrList[0]?.attribute?.id,
                valuesMap: attribute.attrList[0]?.attribute?.valuesMap,
                asyncStatusMessage: 'form.asyncInput.completeStatus',
                unableToVerifyAsync: false
            };

            return {
                ...afs,
                ...updatedProperties
            };
        } else {
            return afs;
        }
    });
};


const clearAttributeSubmitValues = (attributeFormStates: Array<AttributeFormState>, attribute: AttributeInput) => {
    return attributeFormStates.map(afs => {
        if (afs.id === attribute.id) {
            return {
                ...afs,
                inputData: {}
            };
        } else {
            return afs;
        }
    });
};

const getPassStatusProperties = (verifiedAttribute: VerifiedAttribute) => {
    const passProperties = {
        selectedAttributeId: verifiedAttribute.attributeID,
        isWorking: false,
        inBandPinMode: false,
        status: AttributeStatusTypes.PASS,
        isDisabled: true,
        asyncStatusMessage: 'form.asyncInput.completeStatus'
    };
    return passProperties;
}

const getFailStatusProperties = (verifiedAttribute: VerifiedAttribute) => {
    const failProperties = {
        selectedAttributeId: verifiedAttribute.attributeID,
        isWorking: false,
        inBandPinMode: false,
        status: AttributeStatusTypes.FAIL,
        asyncStatusMessage: 'form.asyncInput.failedStatus',
        isDisabled: true
    };
    return failProperties;
};

const getPinStatusProperties = (verifiedAttribute: VerifiedAttribute, attributeFormState: AttributeFormState, attempt: number) => {
    const pinProperties = {
        selectedAttributeId: verifiedAttribute.attributeID,
        inBandPinMode: true,
        isWorking: false,
        status: AttributeStatusTypes.PIN,
        isDisabled: true,
        hasInvalidPin: attempt > 1,
        inputData: { ...attributeFormState.inputData, pincode: '' }
    };
    return pinProperties;
}

const getPickerStatusProperties = (verifiedAttribute: VerifiedAttribute, attributeFormState: AttributeFormState, allowDCUIRetry: boolean) => {
    let pickerProperties: Partial<AttributeFormState> = {
        selectedAttributeId: verifiedAttribute.attributeID,
        isWorking: false,
        inBandPinMode: false,
        status: AttributeStatusTypes.PICKER,
        isDisabled: false,
        isValueFromPicker: true
    };
    if (attributeFormState.isAsync && allowDCUIRetry) {
        pickerProperties = {
            ...pickerProperties,
            unableToVerifyAsync: true,
            status: AttributeStatusTypes.ASYNC_INCOMPLETE,
            asyncStatusMessage: 'form.asyncInput.unableToVerifyStatus',
            valuesMap: {}
        };
    }

    return pickerProperties;
}

const getNewStatusProperties = (verifiedAttribute: VerifiedAttribute, attributeFormState: AttributeFormState, allowDCUIRetry: boolean) => {
    let newProperties: Partial<AttributeFormState> = {
        selectedAttributeId: verifiedAttribute.attributeID,
        isWorking: false,
        inBandPinMode: false,
        status: AttributeStatusTypes.NEW,
        isDisabled: false
    };
    if (attributeFormState.isAsync && allowDCUIRetry) {
        newProperties = {
            ...newProperties,
            unableToVerifyAsync: true,
            status: AttributeStatusTypes.ASYNC_INCOMPLETE,
            asyncStatusMessage: 'form.asyncInput.unableToVerifyStatus',
            valuesMap: {}
        };
    }

    return newProperties;
}

const hasSelectedAttributePickerValue = (attrList: Array<AttrListItem>) => {
    return attrList.every((attr) => attr.assignedTo);
};

const hasNoPreSelectedAttributePickerValue = (attrList: Array<AttrListItem>) => {
    return attrList.every((attr) => !attr.assignedTo);
};

const hasSingleSelectedAttributePickerValue = (attribute: AttributeInput) => {
    return hasSelectedAttributePickerValue(attribute.attrList) && attribute.attrList.length === 1;
};

const hasSingleUnselectedAttributePickerValue = (attribute: AttributeInput) => {
    return !hasSelectedAttributePickerValue(attribute.attrList) && attribute.attrList.length === 1;
};

const hasPreviousPickerValues = (attribute: AttributeInput) => {
    return attribute.attrList.length > 0;
};

const getPickerDefaultAttribute = (attribute: AttributeInput): AttrListItemAttribute => {
    const assignedToAttribute = attribute.attrList.find((attr) => attribute.id === attr.assignedTo);

    if (assignedToAttribute?.attribute) {
        return assignedToAttribute.attribute;
    } else if (hasSingleUnselectedAttributePickerValue(attribute) || hasPreviousPickerValues(attribute)) {
        return attribute.attrList[0]?.attribute || null;
    }
    return null;
};

const getPickerDefaultValue = (attribute: AttributeInput) => {
    return getPickerDefaultAttribute(attribute)?.id || null;
};

const hasDefaultPickerValue = (attribute: AttributeInput) => {
    return !!getPickerDefaultValue(attribute);
};

const shouldDisableInput = (attributeState: AttributeFormState) => {
    if (
        attributeState.status === AttributeStatusTypes.PICKER &&
        !attributeState.isEditable &&
        !attributeState.hasNoPreSelectedAttributePickerValue
    ) {
        return true;
    }

    return false;
};

export const initializeAttributeFormState = (attribute: AttributeInput) => {
    const initialAttributeFormState: AttributeFormState = {
        ...attribute,
        attributeId: attribute.id,
        // Previous selected value ID
        selectedAttributeId: -1,
        // Status of the attribute validation
        status:
            attribute.async || attribute.isAsync ? AttributeStatusTypes.ASYNC_INCOMPLETE : AttributeStatusTypes.NEW,
        attributeDependencyID: attribute.attributeDependencyID,
        oobStatus: attribute.async || attribute.isAsync ? OOBStatusTypes.INCOMPLETE : attribute.oobStatus,
        originalOOBStatus: attribute.oobStatus,
        isAsync: attribute.async || attribute.isAsync,
        isVisible: attribute.visible || attribute.isVisible,
        isNew: attribute.attrList.length === 0,
        isPlaceholder: attribute.isPlaceholder || !!attribute.placeholder,
        isEditable: attribute.isEditable || attribute.editable,
        // Checks whether the attribute has a picker value to preselect
        hasPickerDefaultValue: hasDefaultPickerValue(attribute),
        pickerDefaultValue: getPickerDefaultValue(attribute),
        pickerDefaultAttribute: getPickerDefaultAttribute(attribute),
        // If there is no previously selected picker value
        hasNoPreSelectedAttributePickerValue: hasNoPreSelectedAttributePickerValue(attribute.attrList),
        // If there is one previously available value
        hasPreSelectedSinglePickerValue: hasSingleSelectedAttributePickerValue(attribute),
        // If there are multiple available previously available values
        hasPreviousPickerValues: hasPreviousPickerValues(attribute),
        // If prefilled values are provided
        hasPrefilledValue: Object.keys(attribute.prefillValueMap).length > 0,
        // Used by the UI to show the working state
        isWorking: false,
        // Should this field be disabled after verification
        isDisabled: false,
        // For async attributes, this resets the async data after verification
        unableToVerifyAsync: false,
        // is value set from picker or through the input
        isValueFromPicker: false,
        // capture pin in band for this attribute
        inBandPinMode: false,
        // In band submission error
        inBandVerificationFailed: false,
        // Values returned from async processin
        valuesMap: {},
        // Async status Message
        asyncStatusMessage: 'form.asyncInput.incompleteStatus',
        inputData: null,
        pickerData: null,
        masked: attribute.masked,
        fields: attribute.fields || [],
        isReadOnly: attribute.attrList.length > 0 && attribute.attrList[0].attribute.masked !== ''
    };

    Logger.info(
        `[Attribute-${initialAttributeFormState.attributeId}] initial attribute state`,
        initialAttributeFormState
    );
    Logger.info(
        `[Attribute-${initialAttributeFormState.attributeId}] initial attribute status`,
        initialAttributeFormState.status
    );
    if (
        initialAttributeFormState.hasPickerDefaultValue ||
        initialAttributeFormState.hasPreSelectedSinglePickerValue ||
        initialAttributeFormState.hasPreviousPickerValues
    ) {
        Logger.info(
            `[Attribute-${initialAttributeFormState.attributeId}] Setting picker status for: `,
            attribute.type
        );
        initialAttributeFormState.status = AttributeStatusTypes.PICKER;
        initialAttributeFormState.originalOOBStatus = initialAttributeFormState.oobStatus;
        initialAttributeFormState.oobStatus = OOBStatusTypes.WAIT;
        initialAttributeFormState.asyncStatusMessage = 'form.asyncInput.completeStatus';
    }

    initialAttributeFormState.isDisabled = shouldDisableInput(initialAttributeFormState);

    initialAttributeFormState.inputData = getDefaultFormInputValues(initialAttributeFormState);
    initialAttributeFormState.pickerData = {};
    Logger.info(
        `[Attribute-${initialAttributeFormState.attributeId}] final attribute state`,
        initialAttributeFormState
    );
    Logger.info(
        `[Attribute-${initialAttributeFormState.attributeId}] final attribute status`,
        initialAttributeFormState.status
    );
    return initialAttributeFormState;
};

const setupAttributeDependencies = (attributeListWithState: Array<AttributeFormState>): Array<AttributeFormState> => {
    const attributeIdList = attributeListWithState.map((attr) => attr.attributeId);

    attributeIdList.forEach((attrId: number) => {
        attributeListWithState = attributeListWithState.map((dependentAttribute) => {
            if (
                dependentAttribute.attributeDependencyID &&
                dependentAttribute.attributeDependencyID === attrId
            ) {
                dependentAttribute.status = AttributeStatusTypes.NEW;
                dependentAttribute.oobStatus = dependentAttribute.originalOOBStatus;
            }
            return dependentAttribute;
        });
    });

    return attributeListWithState;
};